/* eslint jsx-a11y/anchor-is-valid: 0 */
import classnamesBind from 'classnames/bind';
import React, {
    useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import reactToString from 'react-to-string';
import Link from '@concur/nui-widgets/Link';
import ScreenReaderMessage from '@concur/nui-widgets/ScreenReaderMessage';
import { withFormatter } from '@concur/nui-intl-runtime';
import { compose, withErrorBoundary, withLogger } from '@concur/core-ui-shell';
import IllustratedMessage from '@concur/nui-widgets/IllustratedMessage';
import ErrorScreen from '@concur/nui-widgets/ErrorScreen';
import PageNotFound from '@concur/nui-widgets/PageNotFound';
import IconButton from '@concur/nui-widgets/IconButton';
import withLoggerData from '../utils/withLoggerData';
import ServiceStatusLink from '../AppFooter/_ServiceStatusLink';

import styles from './AppError.css';

export const ERRORS = {
    aspError: 'AspError',
    browser: 'Browser',
    clientError: 'ClientError',
    comingSoon: 'ComingSoon',
    downloadError: 'DownloadError',
    forbidden: 'Forbidden',
    notFound: 'NotFound',
    profileMoveError: 'ProfileMoveError',
    securityBreach: 'SecurityBreach',
    serverError: 'ServerError',
    timeout: 'Timeout',
};

const CSS_BLOCK = 'sapcnqr-app-error';

const Error = ({
    backFn,
    backUrl,
    className,
    correlationId,
    customDescription,
    customHeader,
    formatter,
    homeUrl,
    links,
    paragraphs,
    type,
    logger,
    dataCenterLocation,
}) => {
    const [announcement, setAnnouncement] = useState('');
    const [copied, setCopied] = useState(false);
    const classnames = classnamesBind.bind(styles);

    const errorType = ERRORS[type] || 'ServerError';
    const hasBackAction = !!backUrl || !!backFn;

    const headerTranslationId = `CoreUI.errorPage_${errorType}.header`;
    const descriptionTranslationId = `CoreUI.errorPage_${errorType}.description`;

    const classes = classnames(CSS_BLOCK, className);

    const sectionRef = useRef(null);
    const linksRef = useRef(null);
    const copyTimer = useRef(null);

    useEffect(() => {
        const previousTitle = document.title;
        if (Object.keys(formatter.preferences.messages).length > 0) {
            document.title = customHeader
                || formatter.formattedMessage({ id: headerTranslationId })
                || previousTitle;
        }

        // try to focus on first helpful link
        if (
            linksRef?.current
            && linksRef.current?.getElementsByTagName
            && linksRef.current.getElementsByTagName('a')?.length > 0
        ) {
            linksRef.current.getElementsByTagName('a')[0].focus();
        }

        return () => {
            document.title = previousTitle;
            clearTimeout(copyTimer.current);
        };
    }, [formatter, customHeader, headerTranslationId]);

    const hasCustomContent = customDescription || paragraphs.length > 0;

    const handleCopy = useCallback(() => {
        if (!correlationId) return;
        navigator.clipboard
            .writeText(correlationId)
            .then(() => {
                setCopied(true);
                // Reset the "Copied!" message after 2 seconds
                copyTimer.current = setTimeout(() => setCopied(false), 2000);
            })
            .catch((err) => {
                logger.error('Failed to copy text: ', err);
            });
    }, [correlationId]);

    const errorContents = (
        <div className={classnames(`${CSS_BLOCK}__details`)}>
            {customDescription && (
                <div className={classnames(`${CSS_BLOCK}__description`)}>{customDescription}</div>
            )}
            {' '}
            {paragraphs.map((paragraph) => (
                <p
                    key={paragraph.text}
                    className={classnames(`${CSS_BLOCK}__description`, paragraph.className)}
                >
                    {paragraph.text}
                    {' '}
                </p>
            ))}
            {' '}
            {!hasCustomContent && (
                <div className={classnames(`${CSS_BLOCK}__description`)}>
                    {formatter.formattedMessage({ id: descriptionTranslationId })}
                </div>
            )}
            {' '}
            {correlationId && (
                <div className={classnames(`${CSS_BLOCK}__error-ctr`)}>
                    <div className={classnames(`${CSS_BLOCK}__error-id`)}>
                        {formatter.formattedMessage({
                            id: 'CoreUI.error.correlationid',
                            values: { correlationId },
                        })}
                        <IconButton
                            iconName="copy"
                            className={classnames(`${CSS_BLOCK}__copy-btn`)}
                            ariaLabel={
                                copied
                                    ? formatter.formattedMessage({
                                        id: 'Common.Copied',
                                    })
                                    : formatter.formattedMessage({
                                        id: 'Common.Copy',
                                    })
                            }
                            onClick={handleCopy}
                        />
                    </div>
                    <ServiceStatusLink dataCenterLocation={dataCenterLocation} errorLink />
                </div>
            )}
        </div>
    );

    const errorContentsAsString = reactToString(errorContents);

    useEffect(() => {
        let announcementTimer;
        if (sectionRef?.current) {
            announcementTimer = setTimeout(() => {
                setAnnouncement(
                    `${document?.title || ''} ${errorContentsAsString} ${formatter.formattedMessage(
                        { id: 'CoreUI.errorPage_Error.helpful_links' },
                    )}`,
                );
            }, 1000);
        }
        return () => {
            clearTimeout(announcementTimer);
        };
    }, [errorContentsAsString]);

    let illustration = <ErrorScreen useResponsiveSizing />;

    if (errorType === ERRORS.forbidden || errorType === ERRORS.notFound) {
        illustration = <PageNotFound useResponsiveSizing />;
    }

    return (
        <section className={classes} ref={sectionRef}>
            <div className={classnames(`${CSS_BLOCK}__inner`)}>
                <div className={classnames(`${CSS_BLOCK}__illustration`)}>
                    <IllustratedMessage
                        size="lg"
                        headline={
                            customHeader || formatter.formattedMessage({ id: headerTranslationId })
                        }
                        sceneContainerClassName={classnames(`${CSS_BLOCK}__illustrated-message`)}
                        description={(
                            <>
                                {errorContents}
                                {errorContentsAsString?.trim() && (
                                    <ScreenReaderMessage isAlert>
                                        {announcement}
                                    </ScreenReaderMessage>
                                )}
                            </>
                        )}
                        additionalContent={
                            (hasBackAction || homeUrl || links.length > 0) && (
                                <nav
                                    aria-label={formatter.formattedMessage({
                                        id: 'CoreUI.errorPage_Error.helpful_links',
                                    })}
                                >
                                    <ul
                                        className={classnames(`${CSS_BLOCK}__helpful-links`)}
                                        ref={linksRef}
                                    >
                                        {hasBackAction && (
                                            <li
                                                className={classnames(
                                                    `${CSS_BLOCK}__helpful-link-item`,
                                                )}
                                            >
                                                <Link
                                                    href={backFn ? '#' : backUrl}
                                                    onClick={backFn}
                                                    className={classnames(`${CSS_BLOCK}__link`)}
                                                >
                                                    {formatter.formattedMessage({
                                                        id: 'CoreUI.errorPage_Error.back',
                                                    })}
                                                </Link>
                                            </li>
                                        )}
                                        {homeUrl && (
                                            <li
                                                className={classnames(
                                                    `${CSS_BLOCK}__helpful-link-item`,
                                                )}
                                            >
                                                <Link
                                                    href={homeUrl}
                                                    className={classnames(`${CSS_BLOCK}__link`)}
                                                >
                                                    {formatter.formattedMessage({
                                                        id: 'CoreUI.errorPage_Error.home',
                                                    })}
                                                </Link>
                                            </li>
                                        )}
                                        {links.map((link) => (
                                            <li
                                                className={classnames(
                                                    `${CSS_BLOCK}__helpful-link-item`,
                                                )}
                                                key={`${link.text}-${link.url}`}
                                            >
                                                <Link
                                                    href={link.url}
                                                    className={classnames(`${CSS_BLOCK}__link`)}
                                                >
                                                    {link.text}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </nav>
                            )
                        }
                    >
                        {illustration}
                    </IllustratedMessage>
                </div>
            </div>
        </section>
    );
};

Error.propTypes = {
    backFn: PropTypes.func,
    backUrl: PropTypes.string,
    className: PropTypes.string,
    correlationId: PropTypes.string,
    customDescription: PropTypes.string,
    dataCenterLocation: PropTypes.string,
    paragraphs: PropTypes.arrayOf(
        PropTypes.shape({
            className: PropTypes.string,
            text: PropTypes.string.isRequired,
        }),
    ),
    customHeader: PropTypes.string,
    homeUrl: PropTypes.string,
    links: PropTypes.arrayOf(
        PropTypes.shape({
            url: PropTypes.string.isRequired,
            text: PropTypes.string.isRequired,
        }),
    ),
    type: PropTypes.oneOf(Object.keys(ERRORS)),
};

Error.defaultProps = {
    links: [],
    paragraphs: [],
    type: 'serverError',
};

export default compose(withLoggerData, withLogger, withFormatter, withErrorBoundary)(Error);
